import type { Editor } from '@tiptap/react'

/**
 * The block palette, shared by the slash menu and the insert toolbar.
 *
 * One list, so the two can never offer different sets — the usual way a slash
 * menu ends up able to insert something the toolbar cannot.
 */
/**
 * The one thing a block needs before it can exist.
 *
 * Most blocks here need nothing: they insert themselves empty and are filled
 * in on the page, where a node view can show what is being decided next to the
 * thing being decided. Two of them cannot — an image by its address has
 * nothing to draw until it has one — and until now the only mechanism for
 * asking was `window.prompt`, which is the operating system interrupting the
 * product. It cannot be styled, it carries no rule, it validates nothing, it
 * covers the caret it is about, and a browser driving the editor can only
 * dismiss it, so nothing had ever inserted one.
 *
 * Declared on the block rather than handled at each surface, because there are
 * two surfaces — the '/' menu and the toolbar's Insert menu — and the usual
 * way they drift apart is one of them learning something the other did not.
 * Both read this and both draw the same form.
 */
export interface BlockAsk {
  /** The field's own label, in the words a writer would use. */
  label: string
  placeholder: string
  /** The rule, on screen while the answer is being typed. */
  note: string
  /** For an answer that is an address rather than a sentence. */
  mono?: boolean
  /** The button that inserts, named after what it inserts. */
  submitLabel: string
}

export interface BlockDef {
  id: string
  label: string
  hint: string
  keywords: string[]
  group: 'Text' | 'Blocks' | 'Media'
  /** What to ask first, for the blocks that cannot exist without an answer. */
  ask?: BlockAsk
  /** `answer` is what `ask` collected, and `''` for a block that asks nothing. */
  run: (editor: Editor, answer: string) => void
}

const paragraph = { type: 'paragraph' as const }

export const BLOCKS: BlockDef[] = [
  {
    id: 'p',
    label: 'Text',
    hint: 'Plain paragraph',
    keywords: ['text', 'paragraph', 'p'],
    group: 'Text',
    run: (e) => e.chain().focus().setNode('paragraph').run(),
  },
  /**
   * Down to four, not six.
   *
   * Four is where real documentation stops — a reference page with parameters
   * inside a section inside a page needs it. Five and six are still reachable by
   * typing `##### `, because a document that arrives with them must be editable;
   * they are just not worth two more rows in a menu somebody reads while
   * writing.
   */
  ...[1, 2, 3, 4].map((level) => ({
    id: `h${level}`,
    label: `Heading ${level}`,
    hint: level === 1 ? 'Page title level' : `Level ${level} section`,
    keywords: [`h${level}`, 'heading', 'title'],
    group: 'Text' as const,
    run: (e: Editor) => e.chain().focus().setNode('heading', { level }).run(),
  })),
  {
    id: 'ul',
    label: 'Bulleted list',
    hint: 'Unordered items',
    keywords: ['list', 'bullet', 'ul'],
    group: 'Text',
    run: (e) => e.chain().focus().toggleList('bulletList', 'listItem').run(),
  },
  {
    id: 'ol',
    label: 'Numbered list',
    hint: 'Ordered items',
    keywords: ['list', 'number', 'ol', 'steps'],
    group: 'Text',
    run: (e) => e.chain().focus().toggleList('orderedList', 'listItem').run(),
  },
  {
    id: 'checklist',
    label: 'Checklist',
    hint: 'Items that can be ticked off',
    keywords: ['check', 'task', 'todo', 'tick', 'box'],
    group: 'Text',
    /**
     * A bullet list whose items carry `checked`, because that is what the
     * document model says a task is. Turning the current block into a list and
     * then setting the attribute keeps this one command rather than a separate
     * node type that would need mapping at the markdown boundary.
     */
    run: (e) =>
      e
        .chain()
        .focus()
        .toggleList('bulletList', 'listItem')
        .updateAttributes('listItem', { checked: false })
        .run(),
  },
  {
    id: 'quote',
    label: 'Quote',
    hint: 'Indented quotation',
    keywords: ['quote', 'blockquote'],
    group: 'Text',
    run: (e) => e.chain().focus().toggleWrap('blockquote').run(),
  },
  {
    id: 'code',
    label: 'Code block',
    hint: 'Fenced code, with a language',
    // 'snippet' left this list when Snippet became a block of its own —
    // one word cannot summon two different things.
    keywords: ['code', 'fence'],
    group: 'Blocks',
    run: (e) => e.chain().focus().setNode('codeBlock').run(),
  },
  {
    id: 'hint',
    label: 'Callout',
    hint: 'Note, tip, warning or danger',
    keywords: ['callout', 'hint', 'note', 'warning', 'tip', 'admonition'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({ type: 'hint', attrs: { type: 'note' }, content: [paragraph] })
        .run(),
  },
  {
    id: 'snippet',
    label: 'Snippet',
    hint: 'A shared block, written once',
    keywords: ['snippet', 'shared', 'reuse', 'include', 'block'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({ type: 'snippet', attrs: { name: '' } })
        .run(),
  },
  {
    id: 'expandable',
    label: 'Expandable',
    hint: 'Collapsible detail section',
    keywords: ['expandable', 'details', 'accordion', 'collapse', 'faq'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({
          type: 'expandable',
          attrs: { title: 'More detail' },
          content: [paragraph],
        })
        .run(),
  },
  {
    id: 'tabs',
    label: 'Tabs',
    hint: 'Alternatives side by side, e.g. iOS and Android',
    keywords: ['tabs', 'tab', 'platform', 'ios', 'android'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({
          type: 'tabs',
          content: [
            { type: 'tab', attrs: { title: 'iOS' }, content: [paragraph] },
            { type: 'tab', attrs: { title: 'Android' }, content: [paragraph] },
          ],
        })
        .run(),
  },
  {
    id: 'steps',
    label: 'Steps',
    hint: 'Numbered procedure, one action per step',
    // `stepper` is what GitBook calls it, so someone migrating will type that.
    keywords: [
      'steps',
      'step',
      'stepper',
      'procedure',
      'howto',
      'guide',
      'walkthrough',
      'numbered',
    ],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({
          type: 'steps',
          content: [
            { type: 'step', attrs: { title: null }, content: [paragraph] },
            { type: 'step', attrs: { title: null }, content: [paragraph] },
          ],
        })
        .run(),
  },
  {
    id: 'figure',
    label: 'Image',
    hint: 'Upload a screenshot, with alt text and a caption',
    keywords: ['image', 'figure', 'screenshot', 'picture', 'photo', 'caption', 'img'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        /**
         * With a paragraph, like every other container here.
         *
         * It used to insert an empty figure, on the reasoning that 33 of 35
         * imported figures have no caption and an empty caption is worse than
         * none. The reasoning was right and the consequence was that the block
         * could not be inserted at all: ProseMirror cannot place an empty
         * non-atom block inside a paragraph, so choosing Image from the menu
         * deleted the trigger text and did nothing. Silently, with no error.
         *
         * The intent is kept where it belongs — the serialiser drops a caption
         * that is only an empty paragraph, so this still writes a figure with no
         * caption.
         */
        .insertContent({
          type: 'figure',
          attrs: { src: null, alt: null, width: null },
          content: [paragraph],
        })
        .run(),
  },
  {
    id: 'embed',
    label: 'Embed',
    hint: 'A video or interactive demo',
    keywords: ['embed', 'video', 'youtube', 'loom', 'vimeo', 'arcade', 'demo', 'iframe'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({ type: 'embed', attrs: { url: null } })
        .run(),
  },
  {
    id: 'file',
    label: 'File attachment',
    hint: 'A downloadable PDF, spec or sample project',
    keywords: ['file', 'attachment', 'download', 'pdf', 'zip', 'attach'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({ type: 'file', attrs: { src: null, name: null }, content: [paragraph] })
        .run(),
  },
  {
    id: 'button',
    label: 'Button',
    hint: 'A call-to-action link',
    keywords: ['button', 'cta', 'link', 'action'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({
          type: 'button',
          attrs: { href: null, style: 'primary' },
          content: [{ type: 'text', text: 'Get started' }],
        })
        .run(),
  },
  {
    id: 'codegroup',
    label: 'Code group',
    hint: 'The same request in cURL, JavaScript, Python…',
    keywords: ['code', 'group', 'tabs', 'curl', 'language'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({
          type: 'codeGroup',
          content: [
            { type: 'codeBlock', attrs: { language: null, meta: null } },
            { type: 'codeBlock', attrs: { language: null, meta: null } },
          ],
        })
        .run(),
  },
  {
    id: 'field',
    label: 'API field',
    hint: 'One parameter or response field, with its type',
    keywords: ['field', 'api', 'parameter', 'param', 'response', 'argument', 'reference'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({
          type: 'field',
          attrs: { name: null, type: null, required: false, default: null, in: null },
          content: [paragraph],
        })
        .run(),
  },
  {
    id: 'cards',
    label: 'Card grid',
    hint: 'Linked cards, for a landing page',
    keywords: ['cards', 'card', 'grid', 'links'],
    group: 'Blocks',
    run: (e) =>
      e
        .chain()
        .focus()
        .insertContent({
          type: 'cards',
          content: [
            { type: 'card', attrs: { title: 'First' }, content: [paragraph] },
            { type: 'card', attrs: { title: 'Second' }, content: [paragraph] },
          ],
        })
        .run(),
  },
  {
    id: 'table',
    label: 'Table',
    hint: '3 columns, with a header row',
    keywords: ['table', 'grid', 'rows'],
    group: 'Blocks',
    run: (e) => {
      const cell = (header: boolean, text: string) => ({
        type: 'tableCell',
        attrs: { header },
        content: text ? [{ type: 'text', text }] : [],
      })
      e.chain()
        .focus()
        .insertContent({
          type: 'table',
          attrs: { align: [null, null, null] },
          content: [
            {
              type: 'tableRow',
              content: [cell(true, 'Column'), cell(true, 'Column'), cell(true, 'Column')],
            },
            { type: 'tableRow', content: [cell(false, ''), cell(false, ''), cell(false, '')] },
          ],
        })
        .run()
    },
  },
  {
    id: 'divider',
    label: 'Divider',
    hint: 'Horizontal rule',
    keywords: ['divider', 'rule', 'hr', 'separator'],
    group: 'Blocks',
    run: (e) => e.chain().focus().insertContent({ type: 'horizontalRule' }).run(),
  },
  {
    id: 'image',
    /**
     * "Image by URL", not "Image".
     *
     * There were two blocks in this menu both labelled Image, and no way to tell
     * from the list which one took a file. This is the inline one, for an image
     * already hosted somewhere; the figure above is the one to reach for.
     */
    label: 'Image by URL',
    hint: 'Inline, for an image already hosted elsewhere',
    keywords: ['image', 'picture', 'url', 'link', 'external', 'img', 'inline'],
    group: 'Media',
    /**
     * One question, asked in the product.
     *
     * It was `window.prompt('Image URL')` — two words over the caret, with no
     * way to say what an address here may be, no way to say that the OTHER
     * Image block is the one that takes a file, and nothing to correct if the
     * address was wrong. There were two prompts before that: the second asked
     * for alt text before there was anything to look at, which is when it is
     * hardest to write, and the figure block has a field for it beside the
     * picture.
     */
    ask: {
      label: 'Image address',
      placeholder: 'https://example.com/screenshot.png',
      note: 'The whole address of an image that is already on the web. To put one from this machine into the page, choose Image instead — it takes a file, and it has alt text and a caption beside the picture.',
      mono: true,
      submitLabel: 'Insert image',
    },
    run: (e, src) => {
      if (!src) return
      e.chain()
        .focus()
        .insertContent({ type: 'image', attrs: { src, alt: null } })
        .run()
    },
  },
]

export const filterBlocks = (query: string): BlockDef[] => {
  const q = query.trim().toLowerCase()
  if (!q) return BLOCKS
  return BLOCKS.filter(
    (b) =>
      // `id`, not just `label`/`keywords`. Every other block's id is already
      // covered by one of those two — "hint" is a keyword of Callout, "tabs" is
      // both its own id and its label — so this was never exercised until
      // "codegroup": a two-word LABEL ("Code group") over a one-word id, with
      // no keyword spelling the id out either. Typing the id itself — the most
      // natural thing to type for a block named after what it is — matched
      // nothing at all.
      b.id.startsWith(q) ||
      b.label.toLowerCase().includes(q) ||
      b.keywords.some((k) => k.startsWith(q)),
  )
}
